import { useState } from "react";
import Layout from "../../../Containers/Layout";
import styles from "./DomainForm.module.scss";
import { Form, DatePicker, Select, Radio, Col, Row, Input } from "antd";
import CustomForm from "../../../CustomComponents/CustomForm";
import { theme } from "../../../theme";
import { Redirect, useParams } from "react-router-dom";
import {
  NOTIFICATION_TYPES,
  openNotification,
} from "../../../Components/Notifications/NotificationsUtils";
import { getUserId } from "../../../utils/utilFunctions";
import ConfirmationModal from "../../../Containers/ConfirmationModal";
import { useTranslation } from "react-i18next";
import { useQuery } from "react-query";
import useDebounce from "../../../Hooks/debounce";
import { getDomain, getFundamentalDomains, getScienceBranches, updateDomain } from "../../../Requests/domain-requests";
import { LearningCompetenceTypeEnum, SimpleStudyCycleEnum } from "../../../Api";
import { addDomain } from "../../../Requests/domain-requests";
import { getFaculties } from "../../../Requests/faculty-requests";
import ProgramCompetenceForm from "../../StudyPrograms/StudyProgramForm/ProgramCompetenceForm";

const { RangePicker } = DatePicker;
const { Group } = Radio;
const { Option } = Select;
const dateFormatList = "YYYY-MM-DD";


const DomainForm = (props: any) => {
  const { t, i18n } = useTranslation();

  const openGetErrorNotification = (_error: any) => {
    openNotification(
      'Eroare',
      'Datele nu au putut fi aduse din server!',
      NOTIFICATION_TYPES.ERROR
    );
  };

  const currentUserId = getUserId();

  const [redirect, setRedirect] = useState(false);

  const [form] = Form.useForm<{
    name: string | null,
    scienceBranchId: string | null,
    studyCycle: SimpleStudyCycleEnum,
    faculties: string[] | null,
    abbreviation: string | null,
    learningKnowledgeOutcomeValues: any[] | null,
    learningSkillOutcomeValues: any[] | null,
    learningResponsabilityOutcomeValues: any[] | null,
  }>();

  const { domainId } = useParams<{ domainId: string }>();

  const { data: domain } = useQuery(["getDomain", domainId], () => getDomain(domainId), {
    onError: (err) => {
      openGetErrorNotification(err);
    },
    onSuccess: (data) => {
      if (data) {
        form.setFieldsValue({
            name: data.name,
            scienceBranchId: data.scienceBranchId,
            studyCycle: data.studyCycle,
            faculties: data.faculties?.map(e => e.id!),
            abbreviation: data.abbreviation,
            learningKnowledgeOutcomeValues: data.learningKnowledgeOutcome || [],
            learningSkillOutcomeValues: data.learningSkillOutcome || [],
            learningResponsabilityOutcomeValues: data.learningResponsabilityOutcome || [],
        });

      }
    },
    refetchOnWindowFocus: false
  });

  const studyCycle = Form.useWatch('studyCycle', form);

  const { data: faculties } = useQuery(['getFaculties'],
        () => {
            return getFaculties(undefined, undefined, 1, 50);
        },
        {
            onError: openGetErrorNotification,
        }
    );

  const filteredFaculties = faculties?.filter((faculty: any) =>
    studyCycle === SimpleStudyCycleEnum.Doctorate
      ? faculty.isDoctoralSchool
      : !faculty.isDoctoralSchool
  );

  const { data: scienceBranches } = useQuery(['getScienceBranches'],
        () => {
            return getScienceBranches(undefined, 1, 50);
        },
        {
            onError: openGetErrorNotification,
        }
    );

  const openSaveErrorNotification = (error: any) => {
    const serverMessage =
      (typeof error?.body === "string" && error.body) ||
      (typeof error?.body?.message === "string" && error.body.message) ||
      (typeof error?.response?.data === "string" && error.response.data) ||
      (typeof error?.response?.data?.message === "string" && error.response.data.message);

    if (serverMessage) {
      openNotification(
        'Eroare!',
        serverMessage,
        NOTIFICATION_TYPES.ERROR
      );
      return;
    }

    if (error.status === 400 || error.status === 409) {
      openNotification(
        'Eroare!',
        'Datele completate nu sunt valide!',
        NOTIFICATION_TYPES.ERROR
      );
    } else {
      openNotification(
        'Eroare!',
        'Propunerea nu a putut fi salvată!',
        NOTIFICATION_TYPES.ERROR
      );
    }
  };

  const handleCancel = () => {
    setRedirect(true);
  };

  const openSuccessNotificationAndRedirect = () => {
    setRedirect(true);
    openNotification(
      'Succes!',
      'Salvarea s-a efectuat cu succes!',
      NOTIFICATION_TYPES.SUCCESS,
      6
    );
  }


  const handleSave = () => {
    const state = form.getFieldsValue();
    console.warn(state);
    
   //TODO validation
    if (state.name !== "") {
      let obj = {
        name: state.name!,
        scienceBranchId: state.scienceBranchId!,
        studyCycle: state.studyCycle!,
        facultyIds: state.faculties,
        abbreviation: state.abbreviation!,
        learningOutcomeTemplates: [
          ...(state.learningKnowledgeOutcomeValues || []).map((e: any) => ({
            ...e,
            type: LearningCompetenceTypeEnum.Knowledge
          })),
          ...(state.learningSkillOutcomeValues || []).map((e: any) => ({
            ...e,
            type: LearningCompetenceTypeEnum.Skill
          })),
          ...(state.learningResponsabilityOutcomeValues || []).map((e: any) => ({
            ...e,
            type: LearningCompetenceTypeEnum.ResponsibilityAutonomy
          })),
        ],
      };
      (domainId ? updateDomain(domainId, obj) : addDomain(obj))
      .then(openSuccessNotificationAndRedirect)
      .catch(openSaveErrorNotification);
    } else {
      openNotification(
        'Eroare!',
        'Datele introduse nu sunt valide!',
        NOTIFICATION_TYPES.ERROR
      );
    }
  };
  

  if (redirect) {
    return <Redirect to="/domenii" />;
  } else {
    return (
      <Layout>
        <div className={`container ${styles.container}`}>
          <CustomForm
            form={form}
            layout={theme.layout}
            action="/domenii"
            boxshadow={"none"}
          >
            <div className={styles.firstLine}>    

              <Row gutter={8}>
                <Col span={24}>
                    <Form.Item
                      name='scienceBranchId'
                      label='Ramura de stiinta:'
                      rules={[
                        {
                          required: true,
                          message: 'Selectarea ramurii de stiinta este obligatorie!',
                          whitespace: false,
                        },
                      ]}
                    >
                      <Select
                        allowClear
                        //mode="multiple"
                        style={{ width: "100%", textAlign: "left" }}
                        placeholder='Alege ramura de stiinta'
                      >
                        {scienceBranches?.data?.map((option: any) => (
                            <Option key={option.id}>{option.name}</Option>
                        ))}
                      </Select>
                    </Form.Item>
                </Col>
                <Col span={14}>
                  <Form.Item
                    name='name'
                    label='Denumirea domeniului de studii:'
                    rules={[
                      {
                        required: true,
                        message: 'Denumirea domeniului de studii este obligatorie!',
                        whitespace: true,
                      },
                    ]}
                    >
                    <Input
                      placeholder='Completează denumirea domeniului de studii'
                      />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item
                    name='abbreviation'
                    label='Abreviere domeniu:'
                    rules={[
                      {
                        required: true,
                        message: 'Abrevierea domeniului este obligatorie!',
                      },
                    ]}
                  >
                    <Input placeholder='Ex: IE, CTI, AIASI' />
                  </Form.Item>
                </Col>
                <Col span={4}>
                    <Form.Item
                      name='studyCycle'
                      label='Ciclu de învățământ:'
                      rules={[
                        {
                          required: true,
                          message: 'Selectarea ciclului de învățământ este obligatorie!',
                          whitespace: false,
                          
                        },
                      ]}
                    >
                      <Select
                        allowClear
                        style={{ width: "100%", textAlign: "left" }}
                        placeholder='Ciclul de învățământ'
                        onChange={(newCycle: SimpleStudyCycleEnum) => {
                          const currentFacultyIds: string[] = form.getFieldValue('faculties') || [];
                          const stillValidIds = (faculties ?? [])
                            .filter((faculty: any) =>
                              newCycle === SimpleStudyCycleEnum.Doctorate
                                ? faculty.isDoctoralSchool
                                : !faculty.isDoctoralSchool
                            )
                            .map((faculty: any) => faculty.id);
                          form.setFieldsValue({
                            faculties: currentFacultyIds.filter((id) => stillValidIds.includes(id)),
                          });
                        }}
                      >
                        <Option value={SimpleStudyCycleEnum.Bachelor}>Licență</Option>
                        <Option value={SimpleStudyCycleEnum.Master}>Masterat</Option>
                        <Option value={SimpleStudyCycleEnum.Doctorate}>Doctorat</Option>
                      </Select>
                    </Form.Item>
                </Col>
                

                {/* select facultati */}
                <Col span={24}>
                    <Form.Item
                      name='faculties'
                      label='Facultăți asociate:'
                      rules={[
                        {
                          required: true,
                          message: 'Selectarea facultăților asociate este obligatorie!',
                          whitespace: false,
                          type: 'array',
                          
                        },
                      ]}
                    >
                      <Select
                        allowClear
                        mode="multiple"
                        style={{ width: "100%", textAlign: "left" }}
                        placeholder='Alege facultățile asociate'
                      >
                        {filteredFaculties?.map((option: any) => (
                            <Option key={option.id}>{option.name}</Option>
                        ))}
                      </Select>
                    </Form.Item>
                </Col>

                <Col span={24}>
                  <div style={{ textAlign: "left", marginTop: "12px" }}>
                    Rezultatele învățării (șabloane ARACIS)
                  </div>
                </Col>
                <Col span={24} style={{ padding: "8px 0px 0px 12px" }}>
                  <div style={{ textAlign: "left", marginBottom: "8px" }}>a. Cunoștințe</div>
                  <ProgramCompetenceForm
                    name="learningKnowledgeOutcomeValues"
                    initialValue={[{ description: "", identificator: "1" }]}
                    rules={true}
                    allowDeleteLastItem={true}
                    identifierPrefix="C"
                    buttonText="Adaugă rezultat - Cunoștințe"
                  />
                </Col>
                <Col span={24} style={{ padding: "8px 0px 0px 12px" }}>
                  <div style={{ textAlign: "left", marginBottom: "8px" }}>b. Abilități</div>
                  <ProgramCompetenceForm
                    name="learningSkillOutcomeValues"
                    initialValue={[{ description: "", identificator: "1" }]}
                    rules={true}
                    allowDeleteLastItem={true}
                    identifierPrefix="A"
                    buttonText="Adaugă rezultat - Abilități"
                  />
                </Col>
                <Col span={24} style={{ padding: "8px 0px 0px 12px" }}>
                  <div style={{ textAlign: "left", marginBottom: "8px" }}>c. Responsabilitate și Autonomie</div>
                  <ProgramCompetenceForm
                    name="learningResponsabilityOutcomeValues"
                    initialValue={[{ description: "", identificator: "1" }]}
                    rules={true}
                    allowDeleteLastItem={true}
                    identifierPrefix="RA"
                    buttonText="Adaugă rezultat - Responsabilitate și Autonomie"
                  />
                </Col>
              </Row>
            </div>
            <div className={styles.secondLine}>
              <div className={styles.firstColumn}>
                <ConfirmationModal
                  nameButton={t('internships.addInternshipForm.cancel')}
                  modalText={t('internships.cancelMessage')}
                  titleButton={''}
                  handleFunction={handleCancel}
                  iconButton={null}
                  shapeButton={''}
                  typeButton={''}
                  disabledProp={false}
                  styleProp={{ background: theme.green, color: theme.white, marginRight: '10%', marginTop: '0%', boxShadow: 'rgba(0, 0, 0, 0.1) 0px 4px 12px' }}
                />
                <ConfirmationModal
                  nameButton={domainId ? t('internships.addInternshipForm.updateButton') : t('internships.addInternshipForm.saveButton')}
                  modalText={t('internships.saveMessage')}
                  titleButton={''}
                  handleFunction={handleSave}
                  iconButton={null}
                  shapeButton={''}
                  typeButton={''}
                  disabledProp={false}
                  styleProp={{ background: theme.secondColor, color: theme.white, marginRight: '10%', marginTop: '0%', boxShadow: 'rgba(0, 0, 0, 0.1) 0px 4px 12px' }}
                />
              </div>
            </div>
          </CustomForm>
        </div>
      </Layout>
    );
  }
};

export default DomainForm;
